// ! it is crucial that (until progress-eater service is properly moved into flash) we ONLY add new tables or new columns to existing tables
// ! if we modify any columns that currently exist, progress-eater service WILL break because type orm will not have the correct schema.

import { sql } from 'drizzle-orm';
import {
  bigint,
  boolean,
  doublePrecision,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  serial,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { subscriptionStatusLiterals, unlockTypesLiterals } from '../types';

export const abTestReadingProgressTesttypeEnum = pgEnum('ab_test_reading_progress_testtype_enum', ['freeContent']);

export const unlockTypes = pgEnum('unlock_types', unlockTypesLiterals);

export const subscriptionStatuses = pgEnum('subscription_statuses', subscriptionStatusLiterals);

const listeningProgressSchema = {
  id: uuid('id')
    .default(sql`uuid_generate_v4()`)
    .primaryKey()
    .notNull(),
  userId: text('userId').notNull(),
  seriesId: text('seriesId').notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  timestamp: bigint('timestamp', { mode: 'number' }),
  isInLibrary: boolean('isInLibrary').default(false).notNull(),
  maxUnlockedEpisodeId: integer('maxUnlockedEpisodeId'),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  nextReleaseTs: bigint('nextReleaseTs', { mode: 'number' }),
  episodeId: integer('episodeId'),
  testBranch: text('testBranch'),
  isCountdownReached: boolean('isCountdownReached').default(false).notNull(),
  episodePosition: doublePrecision('episodePosition'),
  createdAt: timestamp('createdAt', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
  completedAtChapterCnt: integer('completedAtChapterCnt'),
};

export const listeningProgress = pgTable('listening_progress', listeningProgressSchema, (table) => ({
  idxBb54A025330Bbc1F94B560885C: index('IDX_bb54a025330bbc1f94b560885c').on(table.userId, table.seriesId),
  idxDe6Bb905D52Eabdd598B8B6C26: index('IDX_de6bb905d52eabdd598b8b6c26').on(table.id),
  uqBb54A025330Bbc1F94B560885C2: unique('UQ_bb54a025330bbc1f94b560885c2').on(table.userId, table.seriesId),
}));

export const listeningProgressSnapshots = pgTable(
  'listening_progress_snapshots',
  {
    ...listeningProgressSchema,
    subscriptionStatus: subscriptionStatuses('subscription_status').notNull(),
    snapshotDate: timestamp('snapshot_date', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
  },
  (table) => ({
    userIdIndex: index('listening_progress_user_id_index').on(table.userId),
  }),
);

const readingProgressSchemaBase = {
  id: uuid('id')
    .default(sql`uuid_generate_v4()`)
    .primaryKey()
    .notNull(),
  userId: text('userId').notNull(),
  seriesId: text('seriesId').notNull(),
  version: integer('version').default(2).notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  timestamp: bigint('timestamp', { mode: 'number' }),
  isInLibrary: boolean('isInLibrary').default(false).notNull(),
  maxUnlockedEpisodeId: integer('maxUnlockedEpisodeId'),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  nextReleaseTs: bigint('nextReleaseTs', { mode: 'number' }),
  episodeId: integer('episodeId'),
  testBranch: text('testBranch'),
  isCountdownReached: boolean('isCountdownReached').default(false).notNull(),
  contentId: integer('contentId'),
  contentDataId: integer('contentDataId'),
  scrollPosition: doublePrecision('scrollPosition'),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  dataVersion: bigint('dataVersion', { mode: 'number' }),
  createdAt: timestamp('createdAt', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
  completedAtChapterCnt: integer('completedAtChapterCnt'),
};

export const readingProgress = pgTable('reading_progress', readingProgressSchemaBase, (table) => ({
  idx6C7A7Dca7A1D93736D65855Bc3: index('IDX_6c7a7dca7a1d93736d65855bc3').on(table.userId, table.seriesId),
  idx2360621825D1001B80D94996Cb: index('IDX_2360621825d1001b80d94996cb').on(table.id),
  uq6C7A7Dca7A1D93736D65855Bc35: unique('UQ_6c7a7dca7a1d93736d65855bc35').on(table.userId, table.seriesId),
}));

export const readingProgressSnapshots = pgTable(
  'reading_progress_snapshots',
  {
    ...readingProgressSchemaBase,
    subscriptionStatus: subscriptionStatuses('subscription_status').notNull(),
    snapshotDate: timestamp('snapshot_date', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
  },
  (table) => ({
    userIdIndex: index('reading_progress_user_id_index').on(table.userId),
  }),
);

// This table reflects whatever progress state is in the `reading_progress` or `listening_progress` table.
// The endpoints can check this status before performing any swaps which is needed to be idempotent.
export const progressState = pgTable('progress_state', {
  userId: text('user_id').primaryKey().notNull(),
  subscriptionStatus: subscriptionStatuses('subscription_status').notNull(),
});

export const abTestReadingProgress = pgTable(
  'ab_test_reading_progress',
  {
    id: uuid('id')
      .default(sql`uuid_generate_v4()`)
      .primaryKey()
      .notNull(),
    userId: text('userId').notNull(),
    testType: abTestReadingProgressTesttypeEnum('testType').notNull(),
    seriesId: text('seriesId').notNull(),
    maxUnlockedEpisodeId: integer('maxUnlockedEpisodeId'),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    timeToEndCountdownTs: bigint('timeToEndCountdownTs', { mode: 'number' }),
    episodeId: integer('episodeId'),
    isCountdownReached: boolean('isCountdownReached').default(false).notNull(),
    contentId: integer('contentId'),
    contentDataId: integer('contentDataId'),
    scrollPosition: doublePrecision('scrollPosition'),
    createdAt: timestamp('createdAt', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
  },
  (table) => {
    return {
      idxAf771141C0Ef8683D093D15A84: index('IDX_af771141c0ef8683d093d15a84').on(table.userId, table.seriesId),
      idx6Ac5Cd4241309Bea3C46B57105: index('IDX_6ac5cd4241309bea3c46b57105').on(table.id),
      uqAf771141C0Ef8683D093D15A846: unique('UQ_af771141c0ef8683d093d15a846').on(table.userId, table.seriesId),
    };
  },
);

export const userSeriesCovers = pgTable(
  'user_series_covers',
  {
    id: uuid('id')
      .default(sql`uuid_generate_v4()`)
      .primaryKey()
      .notNull(),
    userId: varchar('userId').notNull(),
    seriesId: varchar('seriesId').notNull(),
    abCoverKey: varchar('abCoverKey').notNull(),
  },
  (table) => {
    return {
      idxUseridOnUserSeriesCovers: index('IDX_USERID_ON_USER_SERIES_COVERS').on(table.userId),
      idxSeriesidOnUserSeriesCovers: index('IDX_SERIESID_ON_USER_SERIES_COVERS').on(table.seriesId),
      idxUseridSeriesidOnUserSeriesCovers: uniqueIndex('IDX_USERID_SERIESID_ON_USER_SERIES_COVERS').on(
        table.userId,
        table.seriesId,
      ),
    };
  },
);

export const defaultBranches = pgTable(
  'default_branches',
  {
    id: uuid('id')
      .default(sql`uuid_generate_v4()`)
      .primaryKey()
      .notNull(),
    seriesId: text('seriesId').notNull(),
    userId: text('userId').notNull(),
    defaultBranch: text('defaultBranch').notNull(),
    createdAt: timestamp('createdAt', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
  },
  (table) => {
    return {
      idxUseridSeriesidOnDefaultBranches: uniqueIndex('IDX_USERID_SERIESID_ON_DEFAULT_BRANCHES').on(
        table.seriesId,
        table.userId,
      ),
    };
  },
);

export const migrations = pgTable('migrations', {
  id: serial('id').primaryKey().notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  timestamp: bigint('timestamp', { mode: 'number' }).notNull(),
  name: varchar('name').notNull(),
});

export const migrationProgress = pgTable(
  'migration_progress',
  {
    id: uuid('id')
      .default(sql`uuid_generate_v4()`)
      .primaryKey()
      .notNull(),
    userId: text('userId').notNull(),
    readingProgressStatus: boolean('readingProgressStatus').default(false).notNull(),
    listeningProgressStatus: boolean('listeningProgressStatus').default(false).notNull(),
    createdAt: timestamp('createdAt', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
  },
  (table) => {
    return {
      idx4F25D2115Bc45B38Fa792B100D: index('IDX_4f25d2115bc45b38fa792b100d').on(table.userId),
      idx6Ece6A9Fd9Ab3Ade9520Dea482: index('IDX_6ece6a9fd9ab3ade9520dea482').on(table.id),
      uq4F25D2115Bc45B38Fa792B100Db: unique('UQ_4f25d2115bc45b38fa792b100db').on(table.userId),
    };
  },
);

export const unlockHistory = pgTable('unlock_history', {
  id: serial('id').primaryKey().notNull(),
  userId: text('userId').notNull(),
  seriesId: text('seriesId').notNull(),
  branchId: text('branchId').default('unknown'),
  episodeId: integer('episodeId').notNull(),
  unlockType: unlockTypes('unlock_type').notNull(),
  createdAt: timestamp('createdAt', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
  metadata: jsonb('metadata').$type<{ totalPoints?: number; totalEpisodesToUnlock?: number }>(),
});

export const eaterSchema = {
  unlockHistory,
  migrations,
  migrationProgress,
  readingProgress,
  readingProgressSnapshots,
  listeningProgress,
  listeningProgressSnapshots,
  abTestReadingProgress,
  userSeriesCovers,
  defaultBranches,
  progressState,
};
